import React, { Component } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    IconButton
} from '@material-ui/core';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Label, Alert } from 'reactstrap';
import CircularProgress from '@mui/material/CircularProgress';
import './EditPeopleInContract.css';
import BackendApiService from '../../services/BackendApiService';
import CheckCircleIcon from '../../theme/assets/check_circle.svg';
import HighLightOffIcon from '../../theme/assets/highlight_off.svg';
import CircleExclamationIcon from '../../theme/assets/circle_exclamation.svg';
import { app } from '../../config';

class EditPeopleInContract extends Component {
    constructor(props) {
        super(props);
        this.state = {
            expanded: {},
            loading: true,
            persons: [],
            initialPersons: [],
            invalidFields: {},
            showValidationErrors: false,
            showSuccessMessage: false,
            showError: false,
            hasChanges: false,
            accordionsChanged: {},
        };
    }

    componentDidMount() {
        if (this.props.persons && this.props.persons.length > 0) {
            const filteredPersons = this.props.persons
                .filter(person => person.sexo !== 'E')
                .map((person) => ({
                    rut: person.rut || '',
                    name: person.nombre || '',
                    fatherLastName: person.apellido_paterno || '',
                    motherLastName: person.apellido_materno || '',
                }));

            this.setState({ 
                persons: filteredPersons, 
                initialPersons: JSON.parse(JSON.stringify(filteredPersons)),
                loading: false 
            });
        }
    }
    
    componentDidUpdate(prevProps) {
        if (prevProps.persons !== this.props.persons) {
            const filteredPersons = this.props.persons
                .filter(person => person.sexo !== 'E')
                .map((person) => ({
                    rut: person.rut || '',
                    name: person.nombre || '',
                    fatherLastName: person.apellido_paterno || '',
                    motherLastName: person.apellido_materno || '',
                }));

            this.setState({ 
                persons: filteredPersons, 
                initialPersons: JSON.parse(JSON.stringify(filteredPersons)),
                loading: false,
                showSuccessMessage: false,
                showValidationErrors: false,
                showError: false,
                hasChanges: false
            });
        }
    }

    handleChange = (index) => (event, isExpanded) => {
        this.setState({
            expanded: { ...this.state.expanded, [index]: isExpanded }
        });
    };

    checkForChanges = (persons, initialPersons) => {
        if (!persons || !initialPersons || persons.length !== initialPersons.length) {
            return false;
        }

        for (let i = 0; i < persons.length; i++) {
            const current = persons[i];
            const initial = initialPersons[i];

            if (current.name !== initial.name || 
                current.fatherLastName !== initial.fatherLastName || 
                current.motherLastName !== initial.motherLastName) {
                return true;
            }
        }
        
        return false;
    };

    updateMultiplePersons = async () => {
        this.setState({ loading: true });
        let resp = null;
        const isValidFields = this.validateFields();
        if (!isValidFields) {
            this.setState({ loading: false, showSuccessMessage: false, showValidationErrors: true });
            return;
        }

        const personsToUpdate = this.state.persons.reduce((result, person) => {
            result[person.rut] = {
                nombre: person.name.toUpperCase(),
                apellido_paterno: person.fatherLastName.toUpperCase(),
                apellido_materno: person.motherLastName.toUpperCase(),
                nombre_completo: `${person.name} ${person.fatherLastName} ${person.motherLastName}`.toUpperCase(),
            };
            return result;
        }, {});

        const resetExpanded = {};
        this.props.persons.forEach((_, index) => {
            resetExpanded[index] = false;
        });

        resp = await BackendApiService.updateMultiplePersons(this.props.transferenceCode, personsToUpdate);

        if (!resp || resp.status !== "OK" || app.mockError.editPeopleInContract) {
            this.setState({ loading: false, showSuccessMessage: false, showValidationErrors: false, showError: true, expanded: resetExpanded });
            return;
        }

        if (this.props.onUpdatePersons && typeof this.props.onUpdatePersons === 'function') {
            const updatedPersonsForParent = this.props.persons.map(person => {
                if (person.sexo === 'E') return person;
                
                const updatedPerson = this.state.persons.find(p => p.rut === person.rut);
                if (updatedPerson) {
                    return {
                        ...person,
                        nombre: updatedPerson.name.toUpperCase(),
                        apellido_paterno: updatedPerson.fatherLastName.toUpperCase(),
                        apellido_materno: updatedPerson.motherLastName.toUpperCase(),
                        nombre_completo: `${updatedPerson.name} ${updatedPerson.fatherLastName} ${updatedPerson.motherLastName}`.toUpperCase()
                    };
                }
                return person;
            });
            
            this.props.onUpdatePersons(updatedPersonsForParent);
        }

        this.setState({
            loading: false,
            showValidationErrors: false,
            showSuccessMessage: true,
            expanded: resetExpanded,
            initialPersons: JSON.parse(JSON.stringify(this.state.persons)),
            hasChanges: false,
        });
    }

    validateFields = () => {
        const { persons } = this.state;
        let isValid = true;
        const invalidFields = {};

        persons.forEach((person, personIndex) => {
            invalidFields[personIndex] = {};

            if (!person.name.trim()) {
                isValid = false;
                invalidFields[personIndex].name = true;
            }

            if (!person.fatherLastName.trim()) {
                isValid = false;
                invalidFields[personIndex].fatherLastName = true;
            }
        });

        this.setState({ invalidFields });
        return isValid;
    }

    handlePersonChange = async (index, field, value) => {
        const filteredValue = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '');
        
        const updatedPersons = [...this.state.persons];
        updatedPersons[index] = {
            ...updatedPersons[index],
            [field]: filteredValue,
        };

        if (this.state.showValidationErrors) {
            const updatedInvalidFields = { ...this.state.invalidFields };
            if (updatedInvalidFields[index]) {
                updatedInvalidFields[index][field] = !filteredValue;
                this.setState({ invalidFields: updatedInvalidFields });
            }
        }

        const hasChanges = this.checkForChanges(updatedPersons, this.state.initialPersons);
        
        this.setState({ 
            persons: updatedPersons,
            hasChanges: hasChanges,
            accordionsChanged: { ...this.state.accordionsChanged, [index]: true },
        });
    }

    isFieldInvalid = (personIndex, fieldName) => {
        const { invalidFields, showValidationErrors } = this.state;
        return showValidationErrors &&
            invalidFields[personIndex] &&
            invalidFields[personIndex][fieldName];
    }

    render() {
        const roles = {
            1: 'Vendedor/a',
            0: 'Comprador/a',
        };

        const formatRut = (rut) => {
            if (!rut) return '';

            const rutString = rut.toString();

            const rutSinDV = rutString.slice(0, -1);
            const dv = rutString.slice(-1);

            const rutFormateado = rutSinDV
                .replace(/\./g, '')
                .replace(/^(\d{1,2})(\d{3})(\d{3})$/, '$1.$2.$3');

            return `${rutFormateado}-${dv}`;
        };

        const updatedPersonsMap = this.state.persons.reduce((map, person) => {
            map[person.rut] = person;
            return map;
        }, {});

        return (
            <Dialog
                fullWidth
                maxWidth={'md'}
                open={this.props.open}
                onClose={this.props.handleClose}
                aria-labelledby="max-width-dialog-title"
                className="edit-people-dialog"
                PaperProps={{
                    style: {
                        borderRadius: 20,
                    }
                }}
            >
                <DialogTitle>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" style={{ fontWeight: 700 }}>
                            <BorderColorOutlinedIcon /> &nbsp;&nbsp; Editar nombres de contrato
                        </Typography>
                        <IconButton
                            aria-label="close"
                            onClick={this.props.handleClose}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                </DialogTitle>

                <DialogContent>
                    <Typography gutterBottom>
                        Aquí puedes editar los nombres de las personas individualizadas en el contrato asociado a la patente <strong>{this.props.plate}</strong>. Asegúrate de ingresar el nombre correctamente.
                    </Typography>

                    {this.props.persons?.map((person, index) => {
                        if (person.sexo === 'E') return null;
                        
                        const updatedPerson = updatedPersonsMap[person.rut] || {};
                        const displayName = this.state.showSuccessMessage 
                            ? `${updatedPerson.name || ''} ${updatedPerson.fatherLastName || ''} ${updatedPerson.motherLastName || ''}`
                            : `${person.nombre || ''} ${person.apellido_paterno || ''} ${person.apellido_materno || ''}`;
                        
                        return (
                            <Accordion
                                key={index}
                                onChange={this.handleChange(index)}
                                expanded={!!this.state.expanded[index]}
                            >
                                <AccordionSummary
                                    expandIcon={
                                        this.state.showSuccessMessage && this.state.accordionsChanged[index]
                                            ? <img className="filter-icon" src={CheckCircleIcon} alt="Filter Icon" />
                                            : <ExpandMoreIcon />
                                    }
                                    className={`accordion-summary ${this.state.expanded[index] ? 'expanded' : ''}`}
                                >
                                    <Typography variant="subtitle1">
                                        {person.es_representante ? 'Representante ' : ''}{roles[person.rol]} &nbsp;|&nbsp; {formatRut(person.rut)} &nbsp;|&nbsp; {displayName}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <Label>Nombre</Label>
                                            <TextField
                                                fullWidth
                                                value={updatedPerson.name || ''}
                                                variant="outlined"
                                                onChange={(e) => this.handlePersonChange(index, 'name', e.target.value)}
                                                error={this.isFieldInvalid(index, 'name')}
                                                InputProps={{
                                                    endAdornment: this.isFieldInvalid(index, 'name') ? (
                                                        <img className="input-warning-icon" src={CircleExclamationIcon} alt="Warning Icon" />
                                                    ) : null,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Label>Primer apellido</Label>
                                            <TextField
                                                fullWidth
                                                value={updatedPerson.fatherLastName || ''}
                                                variant="outlined"
                                                onChange={(e) => this.handlePersonChange(index, 'fatherLastName', e.target.value)}
                                                error={this.isFieldInvalid(index, 'fatherLastName')}
                                                InputProps={{
                                                    endAdornment: this.isFieldInvalid(index, 'fatherLastName') ? (
                                                        <img className="input-warning-icon" src={CircleExclamationIcon} alt="Warning Icon" />
                                                    ) : null,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Label>Segundo apellido</Label>
                                            <TextField
                                                fullWidth
                                                value={updatedPerson.motherLastName || ''}
                                                variant="outlined"
                                                onChange={(e) => this.handlePersonChange(index, 'motherLastName', e.target.value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                                {this.state.showValidationErrors && (this.isFieldInvalid(index, 'name') ||
                                    this.isFieldInvalid(index, 'fatherLastName')) && (
                                        <Alert color="warning" className="mb-3">
                                            <img className="filter-icon" src={CircleExclamationIcon} alt="Warning Icon" />
                                            &nbsp;&nbsp;&nbsp;&nbsp;Por favor, completa los datos correctamente para poder guardar cambios.
                                        </Alert>
                                    )}
                            </Accordion>
                        );
                    })}
                    {!this.state.loading && this.state.showSuccessMessage && (
                        <Alert color="success">
                            <img className="filter-icon" src={CheckCircleIcon} alt="Filter Icon" />
                            &nbsp;&nbsp;&nbsp;&nbsp;Los cambios se han guardado exitosamente.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions className={this.state.showSuccessMessage ? 'success-actions' : ''}>
                    {this.state.showSuccessMessage ?
                        <Button color="link" onClick={this.props.handleClose}>Salir</Button> :
                        <>
                            <Button color="link" onClick={this.props.handleClose}>Cancelar y salir</Button>
                            <Button 
                                color="primary" 
                                disabled={this.state.loading || !this.state.hasChanges} 
                                onClick={this.updateMultiplePersons}
                                className={this.state.loading ? 'loading-button' : ''}
                            >
                                {this.state.loading ? <CircularProgress /> : ''}&nbsp;&nbsp;Guardar cambios
                            </Button>
                        </>
                    }
                </DialogActions>
                {this.state.showError && (
                    <DialogContent>
                        <Alert color="danger" className="loading-alert">
                            <img className="filter-icon" src={HighLightOffIcon} alt="Filter Icon" />
                            &nbsp;&nbsp;&nbsp;&nbsp;Ocurrió un error al guardar los cambios. Por favor, intenta nuevamente. Si el problema persiste, contáctanos a soporte@autofact.cl
                        </Alert>
                    </DialogContent>
                )}
            </Dialog>
        );
    }
}

export default EditPeopleInContract;